import { useState, useEffect, useRef } from "react";
import { Button } from "@/shared/components/ui/button";
import { Loader2, ExternalLink } from "lucide-react";
import { supabase } from "@/shared/integrations/supabase/client";
import AiMarkdownRenderer from "@/shared/components/finance/AiMarkdownRenderer";
import { sanitizeHtml } from "@/shared/lib/sanitize";
import { cn } from "@/shared/lib/utils";

let pdfjsLib: typeof import("pdfjs-dist") | null = null;
const getPdfjsLib = async () => {
  if (!pdfjsLib) {
    pdfjsLib = await import("pdfjs-dist");
    pdfjsLib.GlobalWorkerOptions.workerSrc =
      "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.9.155/pdf.worker.min.mjs";
  }
  return pdfjsLib;
};

interface DocumentPreviewProps {
  content: string | null;
  attachmentUrl: string | null;
  title?: string;
  className?: string;
}

type FileType = "pdf" | "docx" | "image" | "text" | "unknown";

const getFileType = (url: string | null): FileType => {
  if (!url) return "text";
  const lower = url.toLowerCase();
  if (lower.endsWith(".pdf")) return "pdf";
  if (lower.endsWith(".docx") || lower.endsWith(".doc")) return "docx";
  if (lower.match(/\.(png|jpg|jpeg|gif|webp|svg)$/)) return "image";
  if (lower.match(/\.(txt|md|csv)$/)) return "text";
  return "unknown";
};

export default function DocumentPreview({
  content,
  attachmentUrl,
  title,
  className,
}: DocumentPreviewProps) {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [loadingUrl, setLoadingUrl] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [pdfDoc, setPdfDoc] = useState<any | null>(null);
  const [pdfPage, setPdfPage] = useState(1);
  const [pdfTotalPages, setPdfTotalPages] = useState(0);
  const [pdfRendering, setPdfRendering] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const [docxHtml, setDocxHtml] = useState<string | null>(null);
  const [docxLoading, setDocxLoading] = useState(false);

  const loadPdf = async (url: string) => {
    try {
      const pdfjs = await getPdfjsLib();
      const pdf = await pdfjs.getDocument({ url }).promise;
      setPdfDoc(pdf);
      setPdfTotalPages(pdf.numPages);
      setPdfPage(1);
    } catch (err) {
      setError("Erro ao carregar PDF.");
      console.error("PDF load error:", err);
    }
  };

  const loadDocx = async (url: string) => {
    setDocxLoading(true);
    try {
      const response = await fetch(url);
      const arrayBuffer = await response.arrayBuffer();
      const mammoth = (await import("mammoth")).default;
      const result = await mammoth.convertToHtml({ arrayBuffer });
      setDocxHtml(result.value);
    } catch (err) {
      setError("Erro ao carregar documento Word.");
      console.error("DOCX load error:", err);
    }
    setDocxLoading(false);
  };

  useEffect(() => {
    if (!attachmentUrl) return;

    let cancelled = false;

    const loadFile = async () => {
      setLoadingUrl(true);
      setError(null);
      setPdfDoc(null);
      setDocxHtml(null);

      const { data, error: urlErr } = await supabase.storage
        .from("institution-documents")
        .createSignedUrl(attachmentUrl, 3600);

      if (cancelled) return;

      if (urlErr || !data?.signedUrl) {
        setError("Não foi possível carregar o arquivo.");
        setLoadingUrl(false);
        return;
      }

      setSignedUrl(data.signedUrl);
      const type = getFileType(attachmentUrl);

      if (type === "pdf") {
        await loadPdf(data.signedUrl);
      } else if (type === "docx") {
        if (!cancelled) await loadDocx(data.signedUrl);
      }

      if (!cancelled) setLoadingUrl(false);
    };

    loadFile();
    return () => { cancelled = true; };
  }, [attachmentUrl]);

  useEffect(() => {
    if (!pdfDoc || !canvasRef.current) return;

    let cancelled = false;
    setPdfRendering(true);

    pdfDoc.getPage(pdfPage).then((page) => {
      if (cancelled) return;
      const viewport = page.getViewport({ scale: 1.5 });
      const canvas = canvasRef.current!;
      const context = canvas.getContext("2d")!;
      canvas.height = viewport.height;
      canvas.width = viewport.width;

      page.render({ canvasContext: context, viewport }).promise.then(() => {
        if (!cancelled) setPdfRendering(false);
      });
    });

    return () => {
      cancelled = true;
    };
  }, [pdfDoc, pdfPage]);

  const fileType = getFileType(attachmentUrl);

  return (
    <div className={cn("space-y-4", className)}>
      {attachmentUrl && (
        <>
          {loadingUrl && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-[#791614]" />
              <span className="ml-2 text-sm text-muted-foreground">
                Carregando arquivo...
              </span>
            </div>
          )}

          {error && (
            <div className="text-center py-8">
              <p className="text-sm text-red-600 mb-3">{error}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (signedUrl)
                    window.open(signedUrl, "_blank", "noopener,noreferrer");
                }}
              >
                <ExternalLink className="h-3.5 w-3.5 mr-1" /> Abrir em nova aba
              </Button>
            </div>
          )}

          {!loadingUrl && !error && fileType === "pdf" && pdfDoc && (
            <div className="flex flex-col items-center gap-3">
              <div className="relative border rounded-lg overflow-hidden bg-[#f9f6f0] shadow-sm w-full">
                {pdfRendering && (
                  <div className="absolute inset-0 flex items-center justify-center bg-[#f9f6f0]/80 z-10">
                    <Loader2 className="h-5 w-5 animate-spin text-[#791614]" />
                  </div>
                )}
                <canvas
                  ref={canvasRef}
                  className="max-w-full h-auto block mx-auto"
                />
              </div>
              {pdfTotalPages > 1 && (
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pdfPage <= 1}
                    onClick={() => setPdfPage((p) => p - 1)}
                  >
                    Anterior
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Página {pdfPage} de {pdfTotalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pdfPage >= pdfTotalPages}
                    onClick={() => setPdfPage((p) => p + 1)}
                  >
                    Próxima
                  </Button>
                </div>
              )}
            </div>
          )}

          {!loadingUrl && !error && fileType === "docx" && (
            <div className="prose prose-sm max-w-none">
              {docxLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin text-[#791614]" />
                </div>
              ) : docxHtml ? (
                <div
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(docxHtml) }}
                  className="[&_table]:border-collapse [&_table]:w-full [&_td]:border [&_td]:border-[#a9abae]/30 [&_td]:p-2 [&_th]:border [&_th]:border-[#a9abae]/30 [&_th]:p-2 [&_th]:bg-[#f9f6f0] [&_h1]:font-display [&_h2]:font-display [&_h3]:font-display"
                />
              ) : (
                <p className="text-muted-foreground">
                  Conteúdo não disponível.
                </p>
              )}
            </div>
          )}

          {!loadingUrl && !error && fileType === "image" && signedUrl && (
            <div className="flex items-center justify-center p-4">
              <img
                src={signedUrl}
                alt={title || "Documento"}
                className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-sm"
              />
            </div>
          )}

          {!loadingUrl && !error && fileType === "unknown" && signedUrl && (
            <div className="text-center py-8">
              <p className="text-sm text-muted-foreground mb-3">
                Preview não disponível para este tipo de arquivo.
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  window.open(signedUrl, "_blank", "noopener,noreferrer")
                }
              >
                <ExternalLink className="h-3.5 w-3.5 mr-1" /> Abrir em nova aba
              </Button>
            </div>
          )}

          {signedUrl && !error && (
            <div className="flex justify-end pt-2 border-t border-[#a9abae]/15">
              <Button
                variant="ghost"
                size="sm"
                className="text-xs text-[#791614]"
                onClick={() =>
                  window.open(signedUrl, "_blank", "noopener,noreferrer")
                }
              >
                <ExternalLink className="h-3 w-3 mr-1" /> Abrir em nova aba
              </Button>
            </div>
          )}
        </>
      )}

      {content && (
        <div>
          {attachmentUrl && (
            <p className="font-accent text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
              Conteúdo Textual
            </p>
          )}
          <AiMarkdownRenderer content={content} className="text-sm" />
        </div>
      )}

      {!content && !attachmentUrl && (
        <p className="text-muted-foreground text-sm text-center py-8">
          Nenhum conteúdo ou arquivo disponível.
        </p>
      )}
    </div>
  );
}
